import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import { useThemeMode } from '../../../../_metronic/partials'
import { getCSSVariableValue } from '../../../../_metronic/assets/ts/_utils'
import { HttpService } from '../../../services/http.services'

type Props = {
  className: string
  chartHeight: string
  apiUrl?: string
}

type DeviceStatusData = {
  totalDevices: number
  onlineDevices: number
  notificationDevices: number
  offlineDevices: number
}

const StatusCountCard: React.FC<Props> = ({
  className, 
  chartHeight, 
  apiUrl = `auth/dashboard/devices/status/count`
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusData, setStatusData] = useState<DeviceStatusData>({
    totalDevices: 0,
    onlineDevices: 0,
    notificationDevices: 0,
    offlineDevices: 0
  })

  const chartColors = ['success', 'danger', 'secondary'] // Added secondary color for offline devices
  const series = statusData.totalDevices > 0
    ? [
        (statusData.onlineDevices / statusData.totalDevices) * 100,
        (statusData.notificationDevices / statusData.totalDevices) * 100,
        (statusData.offlineDevices / statusData.totalDevices) * 100
      ]
    : [0, 0, 0]

  useEffect(() => {
    const fetchDeviceStatus = async () => {
      try {
        setLoading(true)
        const response = await HttpService.get(apiUrl)
        setStatusData({
          totalDevices: response.data.results.totalDevices || 0,
          onlineDevices: response.data.results.onlineDevices || 0,
          notificationDevices: response.data.results.notificationDevices || 0,
          offlineDevices: response.data.results.offlineDevices || 0
        })
        setError(null)
      } catch (err) {
        console.error('Failed to fetch device status:', err)
        setError('Failed to load device status data')
      } finally {
        setLoading(false)
      }
    }

    fetchDeviceStatus()
  }, [apiUrl])

  const refreshChart = () => {
    if (!chartRef.current) return

    const chart = new ApexCharts(
      chartRef.current,
      chartOptions(chartColors, chartHeight, series, statusData.totalDevices)
    )

    chart.render()
    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) chart.destroy()
    }
  }, [chartRef, mode, series, chartColors, statusData.totalDevices])

  if (loading) {
    return (
      <div className={`card ${className}`}>
        <div className='card-body'>
          <div className='d-flex justify-content-center align-items-center' style={{ height: chartHeight }}>
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`card ${className}`}>
        <div className='card-body'>
          <div className='alert alert-danger'>{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className={`card ${className}`}>
      {/* Header */}
      <div className='card-header border-0 py-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Equipment Status</span>
        </h3>
      </div>

      <div className='card-body'>
        <div className='row'>
          {/* Status List */}
          <div className='col-md-6 d-flex align-items-center justify-content-center'>
            <div className='p-5'>
              <p className='fs-6'>
                <span className='badge badge-light-success fs-4'>Online:</span>&nbsp;&nbsp;{statusData.onlineDevices}
              </p>
              <p className='fs-6'>
                <span className='badge badge-light-danger fs-4'>On Alert:</span>&nbsp;{statusData.notificationDevices}
              </p>
              <p className='fs-6'>
                <span className='badge badge-light-secondary fs-4 text-dark'>Offline:</span>&nbsp;{statusData.offlineDevices}
              </p>
            </div>
          </div>

          {/* Chart */}
          <div className='col-md-6'>
            <div ref={chartRef} className='mixed-widget-4-chart' style={{ height: chartHeight }}></div>
          </div>
        </div>
      </div>
    </div>
  )
}

const chartOptions = (
  chartColors: string[],
  chartHeight: string,
  series: number[],
  totalDevices: number
): ApexOptions => {
  const baseColors = chartColors.map(color => getCSSVariableValue('--bs-' + color))
  const lightColors = chartColors.map(color => getCSSVariableValue('--bs-' + color + '-light'))

  return {
    series,
    chart: {
      fontFamily: 'inherit',
      type: 'radialBar',
      height: chartHeight,
      toolbar: { show: false },
    },
    plotOptions: {
      radialBar: {
        startAngle: 0,
        endAngle: 360,
        hollow: {
          size: '30%',
        },
        track: {
          background: lightColors as any,
          strokeWidth: '100%',
        },
        dataLabels: {
          name: {
            show: true,
            fontSize: '16px',
            color: '#6c757d',
            offsetY: -10,
          },
          value: {
            show: true,
            fontSize: '22px',
            fontWeight: 700,
            color: '#000',
            offsetY: 10,
            formatter: function () {
              return totalDevices.toString()
            },
          },
          total: {
            show: true,
            label: 'Total',
            fontSize: '16px',
            fontWeight: 600,
            formatter: function () {
              return totalDevices.toString()
            },
          },
        },
      },
    },
    labels: ['Online', 'On Alert', 'Offline'],
    colors: baseColors,
    stroke: {
      lineCap: 'round',
    },
    tooltip: {
      enabled: true,
      fillSeriesColor: false,
      theme: 'light',
      y: {
        formatter: function (val, { seriesIndex }) {
          return Math.round((val / 100) * totalDevices).toString()
        },
        title: {
          formatter: function (seriesName) {
            return seriesName
          },
        },
      },
    },
  }
}


export { StatusCountCard }